/**
 * Vulnerability-disclosure policy (policy BUILD-10 §8).
 *
 * This is the page `Policy:` in /.well-known/security.txt points at, so it has
 * to be reachable without signing in and has to say something a researcher can
 * actually rely on. The content comes from the same server-side source as the
 * security.txt directives, so the two cannot drift apart.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getTrustOverview, type DisclosurePolicy } from '@/api/trust'

function List({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null
  return (
    <>
      <h2 className="mb-2 mt-8 text-lg font-semibold text-foreground">{title}</h2>
      <ul className="list-inside list-disc space-y-2 text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </>
  )
}

export function TrustDisclosurePage() {
  const [policy, setPolicy] = useState<DisclosurePolicy | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getTrustOverview()
      .then((overview) => {
        if (!cancelled) setPolicy(overview.disclosure)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not load the disclosure policy.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="mb-4 text-3xl font-bold tracking-tight">Vulnerability Disclosure Policy</h1>
      <p className="text-muted-foreground leading-relaxed">
        We welcome reports of security issues in WingCaster. This page explains how to reach us,
        what you can expect from us, and the boundaries we ask you to work within.
      </p>

      {error ? (
        <p role="alert" className="mt-8 text-muted-foreground">{error}</p>
      ) : null}
      {!policy && !error ? <p className="mt-8 text-muted-foreground">Loading…</p> : null}

      {policy ? (
        <>
          <h2 className="mb-2 mt-8 text-lg font-semibold text-foreground">How to report</h2>
          <p className="text-muted-foreground">
            Email{' '}
            <a href={`mailto:${policy.contact_email}`} className="underline underline-offset-4">
              {policy.contact_email}
            </a>
            . Include enough detail to reproduce the issue — the affected URL or endpoint, the steps
            you took, and what you observed. Preferred languages: {policy.preferred_languages}.
          </p>

          <List title="What you can expect from us" items={policy.commitments} />
          <List title="Safe harbour and rules of engagement" items={policy.safe_harbour} />
          <List title="Out of scope" items={policy.out_of_scope} />

          <p className="mt-8 text-sm text-muted-foreground">
            The machine-readable version of our contact details is published at{' '}
            <code className="rounded bg-muted px-1 py-0.5">/.well-known/security.txt</code>. See also
            our <Link to="/trust" className="underline underline-offset-4">Trust Centre</Link>.
          </p>
        </>
      ) : null}
    </div>
  )
}

export default TrustDisclosurePage
